import React, { useMemo, useState } from 'react';
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';

/** Une plage de mois, bornes incluses, au format 'YYYY-MM' attendu par api-core. */
export interface PeriodRange {
	from: string;
	to: string;
}

const MONTHS_OFFERED = 24;

const periodKey = (date: Date): string =>
	`${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;

/**
 * Les mois proposés, du plus récent au plus ancien. Générés depuis la date du
 * jour plutôt que lus en base : proposer uniquement les mois déjà mesurés
 * coûterait une requête de plus pour interdire des choix qui répondent
 * simplement « rien mesuré ».
 */
const offeredMonths = (): string[] => {
	const now = new Date();
	return Array.from({ length: MONTHS_OFFERED }, (_, i) =>
		periodKey(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1)))
	);
};

/**
 * Les mois couverts par une plage, du plus ancien au plus récent.
 *
 * Recalculés depuis la même liste que les sélecteurs : dériver le nombre de mois
 * d'une soustraction de dates ferait diverger le libellé du bandeau de ce que le
 * filtre a réellement demandé.
 */
export const monthsInRange = (range: PeriodRange): string[] => {
	const months = offeredMonths();
	const from = months.indexOf(range.from);
	const to = months.indexOf(range.to);
	if (from < 0 || to < 0) return [];
	return months.slice(to, from + 1).reverse();
};

const PRESETS = [
	{ months: 1, label: 'Mois en cours' },
	{ months: 3, label: '3 mois' },
	{ months: 6, label: '6 mois' },
	{ months: 12, label: '12 mois' },
];

/** La plage courante, avec son état. Le mois en cours par défaut. */
export const usePeriodRange = () => {
	const [range, setRange] = useState<PeriodRange>(() => {
		const current = periodKey(new Date());
		return { from: current, to: current };
	});
	return { range, setRange };
};

const MonthSelect: React.FC<{
	label: string;
	value: string;
	months: string[];
	onChange: (value: string) => void;
}> = ({ label, value, months, onChange }) => (
	// fullWidth={false} n'est pas décoratif : le thème react-admin l'active par
	// défaut sur tous les TextField, et les deux sélecteurs prenaient chacun toute
	// la largeur de la barre, donc passaient à la ligne l'un sous l'autre.
	<TextField
		select
		size="small"
		fullWidth={false}
		label={label}
		value={value}
		onChange={(e) => onChange(e.target.value)}
		sx={{ minWidth: 132 }}
	>
		{months.map((month) => (
			<MenuItem key={month} value={month}>
				{month}
			</MenuItem>
		))}
	</TextField>
);

/**
 * Barre de sélection de la plage. Les deux sélecteurs sont tenus dans l'ordre :
 * une plage inversée ne renverrait aucune ligne, ce qui se lit comme « rien n'a
 * coûté » alors que c'est une erreur de saisie. Le back se protège aussi, mais
 * l'utilisateur ne doit pas pouvoir la produire depuis l'écran.
 *
 * `months` est trié du plus récent au plus ancien : un index plus grand désigne
 * donc un mois plus ancien, ce qui explique le sens des deux comparaisons.
 */
export const PeriodFilter: React.FC<{
	range: PeriodRange;
	onChange: (range: PeriodRange) => void;
}> = ({ range, onChange }) => {
	const months = useMemo(offeredMonths, []);
	const monthCount = months.indexOf(range.from) - months.indexOf(range.to) + 1;

	const applyPreset = (n: number) => onChange({ from: months[n - 1], to: months[0] });

	return (
		<Stack
			direction="row"
			flexWrap="wrap"
			alignItems="center"
			spacing={2}
			useFlexGap
			sx={{ p: 1.5, mb: 3, border: 1, borderColor: 'divider', borderRadius: 1 }}
		>
			<Typography variant="overline" color="text.secondary">
				Période
			</Typography>

			<Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
				{PRESETS.map((preset) => (
					<Button
						key={preset.months}
						size="small"
						variant={monthCount === preset.months ? 'contained' : 'outlined'}
						onClick={() => applyPreset(preset.months)}
					>
						{preset.label}
					</Button>
				))}
			</Stack>

			<Box sx={{ flexGrow: 1 }} />

			<MonthSelect
				label="De"
				value={range.from}
				months={months}
				onChange={(from) =>
					// Choisir un début postérieur à la fin pousse la fin avec lui.
					onChange({
						from,
						to: months.indexOf(from) < months.indexOf(range.to) ? from : range.to,
					})
				}
			/>

			<MonthSelect
				label="À"
				value={range.to}
				months={months}
				onChange={(to) =>
					onChange({
						from: months.indexOf(to) > months.indexOf(range.from) ? to : range.from,
						to,
					})
				}
			/>
		</Stack>
	);
};
